import React, { useState } from 'react';
import { ChevronLeft, Grid3x3, Star, Info, MessageSquare, Compass, Camera, Sparkles, Settings, X, ShieldCheck, Pencil, BadgeCheck, Crown, Loader2, CheckCircle2, Mail, Ban, Lock, LifeBuoy, LogOut, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, Verified } from './shared';
import { profile } from '../mock';

const BIO_ICON = { compass: Compass, camera: Camera, sparkles: Sparkles };
const notify = (msg) => toast.success(msg, { style: { background: '#0f1622', color: '#fff', border: '1px solid #1c2634' } });

function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick} className={`w-11 h-6 rounded-full p-0.5 transition-colors ${on ? 'bg-[#2f8bff]' : 'bg-[#2a3547]'}`}>
      <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-5' : ''}`} />
    </button>
  );
}

function EditProfileSheet({ open, data, onSave, onClose }) {
  const [form, setForm] = useState(data);
  if (!open) return null;
  const setBio = (i, v) => setForm((f) => ({ ...f, bio: f.bio.map((b, idx) => (idx === i ? { ...b, text: v } : b)) }));
  const save = () => { onSave(form); onClose(); notify('Profile updated'); };
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-[440px] bg-[#0c1420] rounded-t-3xl p-5 max-h-[85%] overflow-y-auto app-scroll animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-white">Edit Profile</h3>
          <button onClick={onClose}><X size={22} className="text-[#8b95a5]" /></button>
        </div>
        <label className="text-[12px] text-[#8b95a5]">Name</label>
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full mt-1 mb-3 bg-[#0a0f18] rounded-lg px-3 py-2.5 text-[14px] text-white outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]" />
        <label className="text-[12px] text-[#8b95a5]">Bio</label>
        {form.bio.map((b, i) => (
          <input key={i} value={b.text} onChange={(e) => setBio(i, e.target.value)} className="w-full mt-1 mb-2 bg-[#0a0f18] rounded-lg px-3 py-2 text-[13px] text-white outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]" />
        ))}
        <label className="text-[12px] text-[#8b95a5]">Location</label>
        <input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="w-full mt-1 mb-3 bg-[#0a0f18] rounded-lg px-3 py-2.5 text-[14px] text-white outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]" />
        <label className="text-[12px] text-[#8b95a5]">About</label>
        <textarea value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={4} className="w-full mt-1 mb-4 bg-[#0a0f18] rounded-lg px-3 py-2.5 text-[13px] text-white outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff] resize-none" />
        <button onClick={save} className="w-full py-3 rounded-xl bg-[#2f8bff] text-white text-[15px] font-bold active:scale-[0.98] transition-transform">Save changes</button>
      </div>
    </div>
  );
}

function PublicFigureSheet({ open, onClose }) {
  const [cat, setCat] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('form');
  if (!open) return null;
  const cats = ['Creator', 'Artist', 'Athlete', 'Business', 'Journalist', 'Public Official'];
  const ready = cat && name.trim().length >= 2;
  const pay = () => {
    setStatus('paying');
    setTimeout(() => { setStatus('pending'); setTimeout(() => setStatus('approved'), 2200); }, 1400);
  };
  const close = () => { setStatus('form'); setCat(''); setName(''); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={close}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-[440px] bg-[#0c1420] rounded-t-3xl p-5 max-h-[88%] overflow-y-auto app-scroll animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} className="absolute top-4 right-4"><X size={22} className="text-[#8b95a5]" /></button>

        {status === 'approved' ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-[#122a3a] flex items-center justify-center mx-auto mb-3"><BadgeCheck size={34} className="text-[#2f8bff]" /></div>
            <h3 className="font-display text-xl font-bold text-white">You're Verified!</h3>
            <p className="text-[13px] text-[#8b95a5] mt-1.5">Your Public Figure account has been approved. The blue badge now appears on your profile.</p>
            <button onClick={close} className="w-full mt-5 py-3 rounded-xl bg-[#2f8bff] text-white text-[15px] font-bold">Done</button>
          </div>
        ) : status === 'pending' ? (
          <div className="text-center py-6">
            <Loader2 size={34} className="text-[#2f8bff] animate-spin mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-white">Payment received · Under review</h3>
            <p className="text-[13px] text-[#8b95a5] mt-1.5">Your public-figure application is being reviewed and will be approved shortly.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Crown size={20} className="text-[#f5a524]" />
              <h3 className="font-display text-lg font-bold text-white">Public Figure account</h3>
            </div>
            <p className="text-[13px] text-[#8b95a5] mb-4">Get a verified blue badge, priority support and richer profile tools. Accounts are approved only after a one-time signup payment.</p>

            <label className="text-[12px] text-[#8b95a5]">Full name (as per ID)</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 mb-3 bg-[#0a0f18] rounded-lg px-3 py-2.5 text-[14px] text-white outline-none ring-1 ring-[#1c2634] focus:ring-[#2f8bff]" />

            <label className="text-[12px] text-[#8b95a5]">Category</label>
            <div className="flex flex-wrap gap-2 mt-2 mb-4">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${cat === c ? 'bg-[#2f8bff] text-white' : 'bg-[#141c28] text-[#c4ccd8]'}`}>{c}</button>
              ))}
            </div>

            <div className="flex items-center justify-between rounded-xl bg-[#0a0f18] ring-1 ring-[#1c2634] px-4 py-3 mb-4">
              <span className="text-[13px] text-[#c4ccd8]">One-time signup fee</span>
              <span className="text-[18px] font-bold text-[#f5a524]">₹1000</span>
            </div>

            <button onClick={pay} disabled={!ready || status === 'paying'} className={`w-full py-3.5 rounded-xl text-[15px] font-bold flex items-center justify-center gap-2 transition-colors ${ready ? 'bg-[#2f8bff] text-white active:scale-[0.98]' : 'bg-[#141c28] text-[#5c6675]'}`}>
              {status === 'paying' ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : 'Pay ₹1000 & apply'}
            </button>
            <p className="text-center text-[10px] text-[#5c6675] mt-3">Payment &amp; approval are simulated for this demo.</p>
          </>
        )}
      </div>
    </div>
  );
}

function SettingsSheet({ open, onClose, onEdit, onVerify }) {
  const [twoFA, setTwoFA] = useState(() => localStorage.getItem('emily_2fa') === '1');
  const [loginAlerts, setLoginAlerts] = useState(true);
  if (!open) return null;
  const toggle2fa = () => { const v = !twoFA; setTwoFA(v); localStorage.setItem('emily_2fa', v ? '1' : '0'); };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-[440px] bg-[#0c1420] rounded-t-3xl p-5 animate-pop-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-white">Settings</h3>
          <button onClick={onClose}><X size={22} className="text-[#8b95a5]" /></button>
        </div>
        <button onClick={() => { onClose(); onEdit(); }} className="w-full flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#2a2440] flex items-center justify-center"><Pencil size={17} className="text-[#a78bfa]" /></div>
          <div className="flex-1 text-left">
            <p className="text-[14px] font-semibold text-white">Edit profile</p>
            <p className="text-[12px] text-[#8b95a5]">Update name, bio &amp; details</p>
          </div>
        </button>
        <div className="h-px bg-[#141c28]" />
        <button onClick={() => { onClose(); onVerify(); }} className="w-full flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#3a2a12] flex items-center justify-center"><Crown size={17} className="text-[#f5a524]" /></div>
          <div className="flex-1 text-left">
            <p className="text-[14px] font-semibold text-white flex items-center gap-1.5">Get Verified <BadgeCheck size={14} className="text-[#2f8bff]" /></p>
            <p className="text-[12px] text-[#8b95a5]">Public Figure account · ₹1000</p>
          </div>
        </button>
        <div className="h-px bg-[#141c28]" />
        <div className="flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#122a3a] flex items-center justify-center"><ShieldCheck size={18} className="text-[#2f8bff]" /></div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-white">Two-factor authentication</p>
            <p className="text-[12px] text-[#8b95a5]">Require a one-time code at login</p>
          </div>
          <Toggle on={twoFA} onClick={toggle2fa} />
        </div>
        <div className="h-px bg-[#141c28]" />
        <div className="flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#12322a] flex items-center justify-center"><Info size={18} className="text-[#22c55e]" /></div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-white">Login alerts</p>
            <p className="text-[12px] text-[#8b95a5]">Notify me of new sign-ins</p>
          </div>
          <Toggle on={loginAlerts} onClick={() => setLoginAlerts((v) => !v)} />
        </div>

        <div className="h-px bg-[#141c28]" />
        <button onClick={() => notify('Your email is emma@momos.app · to switch email, transfer via GitHub to a new account')} className="w-full flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#122a3a] flex items-center justify-center"><Mail size={17} className="text-[#2f8bff]" /></div>
          <div className="flex-1 text-left">
            <p className="text-[14px] font-semibold text-white">Account &amp; email</p>
            <p className="text-[12px] text-[#8b95a5]">emma@momos.app</p>
          </div>
          <ChevronRight size={18} className="text-[#5c6675]" />
        </button>
        <div className="h-px bg-[#141c28]" />
        {[
          { Icon: Ban, label: 'Blocked accounts', sub: 'Manage who you\u2019ve blocked', cls: 'bg-[#3a1220] text-[#f43f5e]', msg: 'No blocked accounts' },
          { Icon: Lock, label: 'Privacy', sub: 'Private account, activity status', cls: 'bg-[#2a2440] text-[#a78bfa]', msg: 'Privacy settings opened' },
          { Icon: LifeBuoy, label: 'Help & support', sub: 'FAQs and contact us', cls: 'bg-[#3a2a12] text-[#f5a524]', msg: 'Support center opened' },
        ].map((r) => (
          <React.Fragment key={r.label}>
            <button onClick={() => notify(r.msg)} className="w-full flex items-center gap-3 py-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${r.cls}`}><r.Icon size={17} /></div>
              <div className="flex-1 text-left">
                <p className="text-[14px] font-semibold text-white">{r.label}</p>
                <p className="text-[12px] text-[#8b95a5]">{r.sub}</p>
              </div>
              <ChevronRight size={18} className="text-[#5c6675]" />
            </button>
            <div className="h-px bg-[#141c28]" />
          </React.Fragment>
        ))}
        <button onClick={() => notify('Logged out (demo)')} className="w-full flex items-center gap-3 py-3">
          <div className="w-9 h-9 rounded-lg bg-[#3a1220] flex items-center justify-center"><LogOut size={17} className="text-[#f43f5e]" /></div>
          <p className="text-[14px] font-semibold text-[#f43f5e]">Log out</p>
        </button>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="flex-1 text-center">
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-[12px] text-[#8b95a5]">{label}</p>
    </div>
  );
}

export default function Profile() {
  const [tab, setTab] = useState('photos');
  const [following, setFollowing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [data, setData] = useState({ name: profile.name, bio: profile.bio, location: profile.location, about: profile.about });

  return (
    <div>
      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} onEdit={() => setEditOpen(true)} onVerify={() => setVerifyOpen(true)} />
      <EditProfileSheet open={editOpen} data={data} onSave={setData} onClose={() => setEditOpen(false)} />
      <PublicFigureSheet open={verifyOpen} onClose={() => setVerifyOpen(false)} />

      <div className="relative">
        <div className="h-28 overflow-hidden">
          <img src={profile.cover} alt="" className="w-full h-full object-cover opacity-40 blur-[2px] scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#05070c]" />
        </div>
        <button className="absolute top-3 left-3 p-1.5 rounded-full bg-black/30 backdrop-blur"><ChevronLeft size={22} className="text-white" /></button>
        <button onClick={() => setSettingsOpen(true)} className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 backdrop-blur active:scale-90 transition-transform"><Settings size={20} className="text-white" /></button>
        <div className="flex justify-center -mt-14">
          <div className="p-1 rounded-full bg-[#05070c]">
            <Avatar src={profile.avatar} size={104} className="ring-2 ring-white/90 rounded-full" />
          </div>
        </div>
      </div>

      <div className="px-5 text-center">
        <h1 className="font-display text-2xl font-bold text-white mt-2">{data.name}</h1>
        <div className="flex items-center justify-center gap-4 mt-2 flex-wrap">
          {profile.verifiedUser && <span className="flex items-center gap-1.5 text-[13px] text-[#c4ccd8]"><Verified size={16} /> Verified User</span>}
          {profile.verifiedLocation && <span className="flex items-center gap-1.5 text-[13px] text-[#c4ccd8]"><Verified size={16} /> Verified Location</span>}
        </div>

        <div className="h-px bg-[#141c28] my-4" />

        <div className="flex items-center justify-center gap-x-4 gap-y-1 flex-wrap text-[14px] text-[#dbe1ea]">
          {data.bio.map((b, i) => {
            const Icon = BIO_ICON[b.icon];
            return <span key={i} className="flex items-center gap-1.5"><Icon size={15} className="text-[#2f8bff]" /> {b.text}</span>;
          })}
        </div>

        <div className="h-px bg-[#141c28] my-4" />

        <div className="flex items-center">
          <Stat value={profile.stats.posts} label="Posts" />
          <div className="w-px h-8 bg-[#141c28]" />
          <Stat value={profile.stats.followers} label="Followers" />
          <div className="w-px h-8 bg-[#141c28]" />
          <Stat value={profile.stats.following} label="Following" />
        </div>

        <div className="flex gap-3 mt-4">
          <button className="flex-1 py-2.5 rounded-lg bg-[#2f8bff] text-white text-[14px] font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><MessageSquare size={16} /> Message</button>
          <button onClick={() => setFollowing((v) => !v)} className={`flex-1 py-2.5 rounded-lg text-[14px] font-semibold ring-1 transition-colors active:scale-[0.98] ${following ? 'bg-[#161d29] text-[#8b95a5] ring-[#1c2634]' : 'bg-transparent text-white ring-[#2a3547]'}`}>{following ? 'Following' : 'Follow'}</button>
          <button onClick={() => setEditOpen(true)} className="px-3.5 py-2.5 rounded-lg ring-1 ring-[#2a3547] active:scale-[0.98] transition-transform"><Pencil size={16} className="text-white" /></button>
        </div>
      </div>

      <div className="flex mt-5 border-b border-[#141c28]">
        {[{ k: 'photos', label: 'Photos' }, { k: 'favorites', label: 'Favorites' }, { k: 'about', label: 'About' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`flex-1 py-3 text-[14px] font-semibold border-b-2 transition-colors ${tab === t.k ? 'text-white border-[#2f8bff]' : 'text-[#8b95a5] border-transparent'}`}>{t.label}</button>
        ))}
      </div>

      <div className="p-1">
        {tab === 'photos' && (
          <div className="grid grid-cols-3 gap-1">
            {profile.photos.map((p) => <img key={p} src={p} alt="" className="w-full aspect-square object-cover animate-fade-up" />)}
          </div>
        )}
        {tab === 'favorites' && (
          <div className="grid grid-cols-3 gap-1">
            {profile.favorites.map((p) => <img key={p} src={p} alt="" className="w-full aspect-square object-cover animate-fade-up" />)}
          </div>
        )}
        {tab === 'about' && (
          <div className="p-5">
            <p className="text-[14px] text-[#c4ccd8] leading-relaxed">{data.about}</p>
            <div className="mt-4 flex items-center gap-2 text-[13px] text-[#8b95a5]"><Compass size={15} className="text-[#2f8bff]" /> {data.location}</div>
          </div>
        )}
      </div>
    </div>
  );
}
